// @ts-strict

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { dropdownValues, leadsBackendStatuses } from '@app/core/helpers/constants';
import { checkDateFormat } from '@app/core/helpers/tools';
import { DefaultApiType, IChosenSortValue } from '@app/core/models/core-models';
import { environment } from '@env/environment';
import {
  FilterMenuOptions,
  GraphFilterOption,
  StatisticsColumnManagmentNumberType,
  StatsRequestParams,
  StatsRequestParamsMap
} from '@pages/statistics/shared/interfaces/statistics';
import { StatisticsMapsGraphService } from '@pages/statistics/shared/statistics-graphs/statistics-maps-graph/statistics-maps-graph.service';
import { StatisticsService } from '@pages/statistics/statistics.service';
import { StatisticsGraphsService } from '@services/statistics/statistics-graphs.service';
import { Observable } from 'rxjs';
import { finalize, map } from 'rxjs/operators';
import { AvailableStatisticsTableColumns } from '../../shared/statistics-table/table.service';
import { LeadsGraphsStatisticsResponse, LeadsMapStatisticsResponse, LeadsStatisticsItem, LeadsStatisticsResponse } from './interfaces/leads-statistics';

@Injectable({
  providedIn: 'root'
})
export class LeadsStatisticsService {
  public defaultVisibleColumns: AvailableStatisticsTableColumns[] = ['configurationName', 'created', 'refferingAddress', 'rate', 'countries', 'mlSub'];

  public initialRequestParams: StatsRequestParams = {
    order_field: 'id',
    order_type: 'desc',
    per_page: 100,
    ...this.statisticsService.getInitialRequestDate()
  };

  public columnManagementNumbers: StatisticsColumnManagmentNumberType = {
    leads: [],
    earnings: [],
    others: [31, 36, 37, 38, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 29]
  };

  public dropdownValues: IChosenSortValue[] = [
    dropdownValues[57],
    dropdownValues[58],
    dropdownValues[13],
    dropdownValues[12],
    dropdownValues[19],
    dropdownValues[18],
    dropdownValues[15],
    dropdownValues[14],
    dropdownValues[23],
    dropdownValues[22]
  ];

  public requestParamsMap: StatsRequestParamsMap = {
    campaignMenu: 'programs[]' || 'admin_programs[]',
    campaign_type: 'types[]',
    leads: 'status[]',
    countriesMenu: 'countries[]',
    admin_program_id: 'admin_programs[]',
    refferingMenu: 'referrer',
    statusMenu: 'status[]',
    countries: 'countries[]'
  };

  public graphFilterOptions: GraphFilterOption[] = [
    {
      // name: 'leads',
      name: 'leads_together',
      translation: 'leads',
      status: true,
      fillColor: '#FF8C03',
      borderColor: '#FFF4E6'
    },
    {
      name: 'leads_status_payment_waiting',
      translation: 'leads_status_payment_waiting',
      status: false,
      fillColor: '#FF8C03',
      borderColor: '#FFF4E6'
    },
    {
      name: 'leads_status_rejected',
      translation: 'leads_status_rejected',
      status: false,
      fillColor: '#FF8C03',
      borderColor: '#FFF4E6'
    },
    {
      name: 'leads_status_wait',
      translation: 'leads_status_wait',
      status: false,
      fillColor: '#FF8C03',
      borderColor: '#FFF4E6'
    }
  ];

  public filterMenuOptions: FilterMenuOptions = {
    campaignMenu: [
      {
        category: 'sort',
        values: [dropdownValues[12], dropdownValues[13]]
      },
      {
        category: 'searchInput',
        name: 'campaign_id',
        searchMethod: 'id',
        values: null
      },
      // {
      //   category: 'searchInput',
      //   name: 'admin_program_id',
      //   searchMethod: 'id',
      //   values: null
      // },
      // {
      // category: 'selectWithSearch',
      // name: 'admin_program_id',
      // searchMethod: 'id',
      // values: []
      // },
      {
        category: 'selectWithSearch',
        name: 'campaign_name_or_id',
        type: 'incent',
        values: []
      },
      {
        category: 'checkboxesList',
        values: {
          campaign_type: [
            {
              id: 1,
              text: 'CPL',
              checked: true,
              noTranslation: true,
              category: 'campaign_type',
              fieldType: 'checkbox'
            },
            {
              id: 2,
              text: 'CPS',
              checked: true,
              noTranslation: true,
              category: 'campaign_type',
              fieldType: 'checkbox'
            },
            {
              id: 3,
              text: 'CPA',
              checked: true,
              noTranslation: true,
              category: 'campaign_type',
              fieldType: 'checkbox'
            },
            {
              id: 4,
              text: 'PPI',
              checked: true,
              noTranslation: true,
              category: 'campaign_type',
              fieldType: 'checkbox'
            },
            {
              id: 5,
              text: 'COD',
              checked: true,
              noTranslation: true,
              category: 'campaign_type',
              fieldType: 'checkbox'
            }
          ]
        }
      }
    ],
    statusMenu: [
      {
        category: 'sort',
        values: [dropdownValues[18], dropdownValues[19]]
      },
      {
        category: 'checkboxesList',
        values: {
          leads: [
            {
              id: leadsBackendStatuses.toPay,
              column: 'leadToPay',
              text: 'toPay',
              icon: 'money-bag-alt-bold',
              checked: true,
              fieldType: 'checkbox',
              category: 'leads'
            },
            {
              id: leadsBackendStatuses.accepted,
              column: 'leadAccepted',
              text: 'accepted',
              icon: 'money-protection',
              checked: true,
              fieldType: 'checkbox',
              category: 'leads'
            },
            {
              id: leadsBackendStatuses.pending,
              column: 'leadPending',
              text: 'pending',
              icon: 'money-time',
              checked: true,
              fieldType: 'checkbox',
              category: 'leads'
            },
            {
              id: leadsBackendStatuses.rejected,
              column: 'leadRejected',
              text: 'rejected',
              icon: 'cross-circle',
              checked: true,
              fieldType: 'checkbox',
              category: 'leads'
            }
          ]
        }
      }
    ],
    configurationNameMenu: [
      {
        category: 'sort',
        values: [dropdownValues[14], dropdownValues[15]]
      }
    ],
    leadIDMenu: [
      {
        category: 'sort',
        values: [dropdownValues[57], dropdownValues[58]]
      }
    ],
    rateMenu: [
      {
        category: 'sort',
        values: [dropdownValues[22], dropdownValues[23]]
      },
      {
        category: 'slider',
        name: 'rate',
        rangeStep: 0.01,
        minValue: 0,
        maxValue: 1000000,
        currentMinValue: 0,
        currentMaxValue: 1000000,
        fieldType: 'slider',
        paramFrom: 'amount_from',
        paramTo: 'amount_to',
        unit: 'currency'
      }
    ],
    countriesMenu: [
      {
        category: 'countriesSelect',
        name: 'countries',
        values: []
      }
    ],
    mlSubMenu: Array.from({ length: 5 }, (_e, i) => ({
      category: 'searchInput',
      name: `ml_sub${i + 1}`,
      searchMethod: 'name',
      values: null
    })),
    refferingMenu: [
      {
        category: 'searchInput',
        name: 'referring_address',
        searchMethod: 'name',
        values: null
      }
    ]
  };

  constructor(
    private readonly http: HttpClient,
    private readonly statisticsGraphsService: StatisticsGraphsService,
    private readonly statisticsMapsGraphService: StatisticsMapsGraphService,
    private readonly statisticsService: StatisticsService
  ) {}

  public getLeadsStatisticsData = (requestParams: StatsRequestParams): Observable<{ error: boolean } & Required<LeadsStatisticsResponse>> => {
    const params = {
      ...requestParams,
      date_from: checkDateFormat(requestParams.date_from),
      date_to: checkDateFormat(requestParams.date_to)
    };
    return this.http.get<DefaultApiType<LeadsStatisticsResponse>>(environment.apiUrl + `/statistics/leads`, { params }).pipe(
      map(({ status_code, data }) => {
        if (status_code && status_code === 200 && data && data.leads && data.pagination && data.summary) {
          return { error: false, leads: data.leads, pagination: data.pagination, summary: data.summary };
        }
        if (status_code && status_code === 200 && data && data.leads && data.pagination && params.summary === 0) {
          return { error: false, leads: data.leads, pagination: data.pagination, summary: [] };
        }
        return { error: true, leads: [], pagination: {}, summary: [] };
      })
    );
  };

  public getLeadsMapStatisticsData = (requestParams: StatsRequestParams): Observable<{ error: boolean } & Required<LeadsMapStatisticsResponse>> => {
    this.statisticsMapsGraphService.setIsLoading(true);

    const { order_field, order_type, page, ...rest } = requestParams;

    const params = {
      ...rest,
      date_from: checkDateFormat(requestParams.date_from),
      date_to: checkDateFormat(requestParams.date_to)
    };

    return this.http.get<DefaultApiType<LeadsMapStatisticsResponse>>(environment.apiUrl + `/statistics/leads/map`, { params }).pipe(
      map(({ status_code, data }) => {
        if (status_code && status_code === 200 && data && data.map_statistics) {
          const { map_statistics } = data;

          return { error: false, map_statistics };
        }

        return { error: true, map_statistics: [] };
      }),
      finalize(() => this.statisticsMapsGraphService.setIsLoading(false))
    );
  };

  public getLeadsGrahpsStatisticsData = (requestParams: StatsRequestParams): Observable<{ error: boolean } & Required<LeadsGraphsStatisticsResponse>> => {
    this.statisticsGraphsService._loadingGraphDataSubject.next(true);

    const { order_field, order_type, page, ...rest } = requestParams;

    const params = {
      ...rest,
      date_from: checkDateFormat(requestParams.date_from),
      date_to: checkDateFormat(requestParams.date_to)
    };

    return this.http.get<DefaultApiType<LeadsGraphsStatisticsResponse>>(environment.apiUrl + `/statistics/leads/chart`, { params }).pipe(
      map(({ status_code, data }) => {
        if (status_code && status_code === 200 && data && data.chart_statistics) {
          const { chart_statistics } = data;

          return { error: false, chart_statistics };
        }

        return { error: true, chart_statistics: [] };
      }),
      finalize(() => this.statisticsGraphsService._loadingGraphDataSubject.next(false))
    );
  };
}

[
  {
    country: 'PL',
    referrer: 'http://m.facebook.com/',
    created_at: '2024-09-18T11:36:04.000000Z',
    config_name: ''
  },
  {
    country: 'PL',
    referrer: 'https://locking2.eu/',
    created_at: '2024-04-12T07:00:13.000000Z',
    config_name: ''
  },
  {
    country: 'DE',
    referrer: 'https://locking2.eu/',
    created_at: '2024-04-12T07:16:13.000000Z',
    config_name: ''
  }
];
